import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Heading, Section, Tile, Button, Tag, TextArea, Checkbox } from '@carbon/react';
import {
  ArrowLeft,
  UserAvatar,
  Time,
  Restaurant,
  Medication,
  Events,
  Activity,
  User,
  Checkmark,
  Close,
  Edit,
  ChevronDown,
  ChevronUp
} from '@carbon/icons-react';
import PageContainer from '../components/PageContainer';

const ResidentSchedule = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';
  
  const [selectedDate] = useState('05.11.2025');
  const [newNote, setNewNote] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    dailyPlan: true,
    weeklyActivities: true,
    staffNotes: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Mock data
  const scheduleData = {
    resident: {
      firstName: 'Milica',
      lastName: 'Jovanović',
      room: '101'
    },
    dailySchedule: [
      { 
        time: '07:00', 
        activity: 'Buđenje i jutarnja higijena', 
        type: 'routine',
        completed: true,
        note: ''
      },
      { 
        time: '08:00', 
        activity: 'Doručak', 
        type: 'meal',
        completed: true,
        note: 'Pojeo sve'
      },
      { 
        time: '09:00', 
        activity: 'Terapija - uzimanje lekova', 
        type: 'therapy',
        completed: true,
        note: ''
      },
      { 
        time: '10:00', 
        activity: 'Jutarnja gimnastika', 
        type: 'activity',
        completed: true,
        note: 'Aktivno učestvovao'
      },
      { 
        time: '11:00', 
        activity: 'Šetnja u bašti', 
        type: 'walk',
        completed: true,
        note: ''
      },
      { 
        time: '13:00', 
        activity: 'Ručak', 
        type: 'meal',
        completed: true,
        note: ''
      },
      { 
        time: '15:00', 
        activity: 'Društvene aktivnosti - karte', 
        type: 'social',
        completed: false,
        note: ''
      },
      { 
        time: '17:00', 
        activity: 'Slobodno vreme', 
        type: 'free',
        completed: false,
        note: ''
      },
      { 
        time: '19:00', 
        activity: 'Večera', 
        type: 'meal',
        completed: false,
        note: ''
      },
      { 
        time: '20:00', 
        activity: 'Terapija - uzimanje lekova', 
        type: 'therapy',
        completed: false,
        note: ''
      },
      { 
        time: '22:00', 
        activity: 'Priprema za spavanje', 
        type: 'routine',
        completed: false,
        note: ''
      }
    ],
    weeklyActivities: [
      { day: 'Ponedeljak', activity: 'Jutarnja gimnastika', time: '10:00', participation: 'active' },
      { day: 'Ponedeljak', activity: 'Muzička terapija', time: '15:00', participation: 'passive' },
      { day: 'Utorak', activity: 'Jutarnja gimnastika', time: '10:00', participation: 'active' },
      { day: 'Utorak', activity: 'Radna terapija', time: '14:00', participation: 'absent' },
      { day: 'Sreda', activity: 'Jutarnja gimnastika', time: '10:00', participation: 'active' },
      { day: 'Sreda', activity: 'Filmski popodne', time: '16:00', participation: 'active' },
      { day: 'Četvrtak', activity: 'Jutarnja gimnastika', time: '10:00', participation: 'active' },
      { day: 'Četvrtak', activity: 'Likovne radionice', time: '15:00', participation: 'passive' },
      { day: 'Petak', activity: 'Jutarnja gimnastika', time: '10:00', participation: 'active' },
      { day: 'Petak', activity: 'Društvene igre', time: '14:00', participation: 'active' }
    ],
    staffNotes: [
      { 
        time: '14:30', 
        author: 'Sestra Ana', 
        note: 'Imao posetu sina Marka, doneo voće i slatkiše',
        type: 'visit'
      },
      { 
        time: '10:15', 
        author: 'Fizioterapeut Marko', 
        note: 'Odličan napredak u vežbama ravnoteže',
        type: 'progress'
      },
      { 
        time: '08:30', 
        author: 'Sestra Jovana', 
        note: 'Nije hteo da jede doručak, pojeo samo jogurt',
        type: 'meal'
      }
    ]
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'meal': return <Restaurant size={20} />;
      case 'therapy': return <Medication size={20} />;
      case 'activity': return <Activity size={20} />;
      case 'social': return <User size={20} />;
      case 'walk': return <Events size={20} />;
      default: return <Time size={20} />;
    }
  };

  const getActivityColor = (type) => {
    switch(type) {
      case 'meal': return '#0f62fe';
      case 'therapy': return '#da1e28';
      case 'activity': return '#9fb746';
      case 'social': return '#8a3ffc';
      case 'walk': return '#0072c3';
      default: return '#525252';
    }
  };

  const getParticipationBadge = (participation) => {
    switch(participation) {
      case 'active':
        return <Tag type="green" size="sm"><Checkmark size={12} /> Aktivno</Tag>;
      case 'passive':
        return <Tag type="blue" size="sm">Pasivno</Tag>;
      case 'absent':
        return <Tag type="red" size="sm"><Close size={12} /> Odsutan</Tag>;
      default:
        return <Tag type="gray" size="sm">N/A</Tag>;
    }
  };

  return (
    <PageContainer>
      {/* Header sa Nazad button-om i profilom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ArrowLeft}
          hasIconOnly
          iconDescription="Nazad"
          onClick={() => navigate(`/residents/${id}`)}
        />
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.75rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
          borderRadius: '4px',
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={() => navigate(`/residents/${id}`)}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '4px',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: 'rgba(159, 183, 70, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UserAvatar size={20} style={{ color: '#9fb746' }} />
          </div>
          <div>
            <div style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: isDarkMode ? '#f4f4f4' : 'inherit'
            }}>
              {scheduleData.resident.firstName} {scheduleData.resident.lastName}
            </div>
            <div style={{ 
              fontSize: '0.7rem', 
              opacity: 0.7,
              color: isDarkMode ? '#d0d0d0' : 'inherit'
            }}>
              Soba {scheduleData.resident.room}
            </div>
          </div>
        </div>
      </div>

      {/* Dnevni plan - Timeline dizajn */}
      <Section style={{ marginBottom: '2rem' }}>
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '0.75rem',
              borderBottom: expandedSections.dailyPlan ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('dailyPlan')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>
                Dnevni plan - {selectedDate}
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                  {scheduleData.dailySchedule.filter(s => s.completed).length}/{scheduleData.dailySchedule.length}
                </span>
                <div style={{ 
                  width: '60px', 
                  height: '6px', 
                  backgroundColor: 'rgba(159, 183, 70, 0.2)', 
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(scheduleData.dailySchedule.filter(s => s.completed).length / scheduleData.dailySchedule.length) * 100}%`,
                    height: '100%',
                    backgroundColor: '#9fb746',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            </div>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.dailyPlan ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('dailyPlan');
              }}
            >
              {expandedSections.dailyPlan ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>

          <div style={{
            maxHeight: expandedSections.dailyPlan ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.dailyPlan 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.dailyPlan ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              {/* Kompaktan grid */}
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '0.5rem'
              }}>
                {scheduleData.dailySchedule.map((item, index) => (
                  <div 
                    key={index}
                    style={{ 
                      padding: '0.625rem',
                      backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
                      border: `1px solid ${item.completed ? 'rgba(159, 183, 70, 0.3)' : 'rgba(0,0,0,0.1)'}`,
                      borderLeft: `3px solid ${item.completed ? getActivityColor(item.type) : '#e0e0e0'}`,
                      borderRadius: '4px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                      <div style={{ color: getActivityColor(item.type), flexShrink: 0 }}>
                        {getActivityIcon(item.type)}
                      </div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.6, color: isDarkMode ? '#d0d0d0' : 'inherit' }}>
                        {item.time}
                      </div>
                    </div>
                    <div style={{ 
                      fontWeight: '600', 
                      fontSize: '0.8125rem',
                      color: isDarkMode ? '#f4f4f4' : 'inherit',
                      lineHeight: '1.3'
                    }}>
                      {item.activity}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </Section>

      {/* Nedeljne aktivnosti - Grid cards */}
      <Section style={{ marginBottom: '2rem' }}>
        <Tile style={{ padding: '0', boxShadow: '0 2px 8px rgba(159, 183, 70, 0.15)' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '0.75rem',
              borderBottom: expandedSections.weeklyActivities ? '1px solid rgba(128,128,128,0.1)' : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('weeklyActivities')}
          >
            <h3 style={{ margin: 0, fontSize: '1rem' }}>
              Nedeljne aktivnosti i učešće
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.weeklyActivities ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('weeklyActivities');
              }}
            >
              {expandedSections.weeklyActivities ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
        
          <div style={{
            maxHeight: expandedSections.weeklyActivities ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.weeklyActivities 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.weeklyActivities ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: '1rem' 
              }}>
                {['Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak'].map(day => (
                  <Tile 
                    key={day}
                    style={{ 
                      padding: '1rem',
                      background: isDarkMode 
                        ? 'linear-gradient(135deg, rgba(159, 183, 70, 0.15) 0%, #2d2d2d 100%)'
                        : 'linear-gradient(135deg, rgba(159, 183, 70, 0.05) 0%, white 100%)',
                      border: '1px solid rgba(159, 183, 70, 0.2)'
                    }}
                  >
                    <div style={{ 
                      fontWeight: '600', 
                      marginBottom: '1rem',
                      paddingBottom: '0.5rem',
                      borderBottom: '2px solid rgba(159, 183, 70, 0.3)',
                      color: '#9fb746'
                    }}>
                      {day}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {scheduleData.weeklyActivities
                        .filter(act => act.day === day)
                        .map((act, idx) => (
                          <div 
                            key={idx}
                            style={{ 
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0.5rem',
                              backgroundColor: isDarkMode ? '#262626' : 'white',
                              borderRadius: '4px',
                              border: '1px solid rgba(0,0,0,0.05)'
                            }}
                          >
                            <div>
                              <div style={{ 
                                fontSize: '0.8125rem', 
                                fontWeight: '500',
                                color: isDarkMode ? '#f4f4f4' : 'inherit'
                              }}>
                                {act.activity}
                              </div>
                              <div style={{ 
                                fontSize: '0.75rem', 
                                opacity: 0.6, 
                                marginTop: '0.125rem',
                                color: isDarkMode ? '#d0d0d0' : 'inherit'
                              }}>
                                {act.time}
                              </div>
                            </div>
                            {getParticipationBadge(act.participation)}
                          </div>
                        ))}
                    </div>
                  </Tile>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </Section>

      {/* Napomene osoblja */}
      <Section style={{ marginBottom: '2rem' }}>
        <Heading style={{ margin: '0 0 1.5rem 0', fontSize: '1.25rem' }}>
          Napomene osoblja
        </Heading>

        {/* Forma za novu napomenu */}
        <Tile style={{ 
          padding: '1rem',
          marginBottom: '1.5rem',
          background: 'linear-gradient(135deg, rgba(159, 183, 70, 0.08) 0%, rgba(159, 183, 70, 0.02) 100%)',
          border: '1px dashed rgba(159, 183, 70, 0.3)'
        }}>
          <TextArea
            labelText="Dodaj napomenu"
            placeholder="Unesite napomenu o rezidentu..."
            rows={3}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <Button size="sm">
              Sačuvaj napomenu
            </Button>
            <Button size="sm" kind="secondary">
              Otkaži
            </Button>
          </div>
        </Tile>

        {/* Lista napomena */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {scheduleData.staffNotes.map((note, index) => (
            <Tile 
              key={index}
              style={{ 
                padding: '1rem',
                borderLeft: `4px solid ${
                  note.type === 'visit' ? '#8a3ffc' : 
                  note.type === 'progress' ? '#9fb746' : 
                  '#0f62fe'
                }`,
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                backgroundColor: isDarkMode ? '#2d2d2d' : 'white'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <div style={{ 
                    fontWeight: '600', 
                    fontSize: '0.875rem',
                    color: isDarkMode ? '#f4f4f4' : 'inherit'
                  }}>
                    {note.author}
                  </div>
                  <Tag 
                    type={
                      note.type === 'visit' ? 'purple' : 
                      note.type === 'progress' ? 'green' : 
                      'blue'
                    }
                    size="sm"
                  >
                    {note.type === 'visit' ? 'Poseta' : 
                     note.type === 'progress' ? 'Napredak' : 
                     'Ishrana'}
                  </Tag>
                </div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  opacity: 0.6,
                  color: isDarkMode ? '#d0d0d0' : 'inherit'
                }}>
                  {note.time}
                </div>
              </div>
              <div style={{ 
                fontSize: '0.875rem', 
                lineHeight: '1.5',
                color: isDarkMode ? '#e8e8e8' : 'inherit'
              }}>
                {note.note}
              </div>
            </Tile>
          ))}
        </div>
      </Section>
    </PageContainer>
  );
};

export default ResidentSchedule;
